import React, { ReactNode, useContext } from 'react';
import { StyleSheet, TouchableHighlight, View } from 'react-native';

import DyteText from './DyteText';
import { States } from '../types/props';
import { IconPack, defaultIconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { SvgXml } from 'react-native-svg';
import { useDyteSelector } from '@dytesdk/react-native-core';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  size,
  variant,
  iconPack = defaultIconPack,
  states,
  t,
  children,
}: {
  size?: 'lg' | 'md' | 'sm' | 'xl';
  variant?: 'button' | 'horizontal';
  iconPack?: IconPack;
  states?: States;
  t?: DyteI18n;
  children?: ReactNode;
}) {
  const defaultLang = useLanguage();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  states = states ?? storeStates;
  const waiting = useDyteSelector((m) => m.participants.waitlisted);
  const stageStatus = useDyteSelector((m) => m.stage.status);
  const stageRequested = useDyteSelector((m) =>
    m.participants.joined
      .toArray()
      .filter((p) => p.stageStatus === 'REQUESTED_TO_JOIN_STAGE')
  );
  const totalRequests = waiting.toArray().length + stageRequested.length;
  let buttonSize = 25;
  let buttonHorizontal = false;
  const strReplaceAll = (str: string, find: string, replace: string) => {
    return str.replace(new RegExp(find, 'g'), replace);
  };
  switch (size) {
    case 'md':
      buttonSize = 50;
      break;
    case 'lg':
      buttonSize = 100;
      break;
    case 'xl':
      buttonSize = 150;
      break;
  }
  switch (variant) {
    case 'horizontal':
      buttonHorizontal = true;
      break;
  }
  const styles = StyleSheet.create({
    container: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    iconStyle: {
      alignItems: 'center',
      justifyContent: 'center',
      height: 56,
      width: 56,
      flexDirection: 'row',
    },
    newItems: {
      position: 'absolute',
      top: 0,
      right: 0,
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.text,
      height: 20,
      width: 20,
      borderRadius: 13,
      backgroundColor: colors.brand[300],
    },
  });
  return (
    <View style={styles.container}>
      {buttonHorizontal && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          onPress={() => {
            setStates({
              ...states,
              activeMoreMenu: !states.activeMoreMenu,
            });
          }}
          style={styles.iconStyle}
        >
          <View style={styles.iconContainer}>
            <SvgXml
              xml={strReplaceAll(
                iconPack.horizontal_dots,
                'currentColor',
                colors.text
              )}
              width={buttonSize}
              height={buttonSize}
            />
            {/* eslint-disable-next-line react-native/no-inline-styles */}
            <DyteText style={{ fontSize: 12, margin: 4 }}>More</DyteText>
            {stageStatus === 'ON_STAGE' && totalRequests > 0 && (
              <View style={styles.newItems}>
                <DyteText>1</DyteText>
              </View>
            )}
          </View>
        </TouchableHighlight>
      )}
      {!buttonHorizontal && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          onPress={() => {
            setStates({
              ...states,
              activeMoreMenu: !states.activeMoreMenu,
            });
          }}
          style={styles.iconStyle}
        >
          <SvgXml
            xml={strReplaceAll(
              !states.activeMoreMenu
                ? iconPack.chevron_up
                : iconPack.chevron_down,
              'currentColor',
              colors.text
            )}
            width={buttonSize}
            height={buttonSize}
          />
        </TouchableHighlight>
      )}
      {children}
    </View>
  );
}
